import { ApiError } from "@/api";
import type {
  MailDomainRecord,
  MailDomainSending,
  MailDomainSendingState,
  MailDomainState,
  MailDomainView,
} from "@/api";
import type { StatusTone } from "@/ds";
import type { ToastTone } from "@/notifications/toast-queue";

/**
 * Pure decisions behind the mail-domain screen (#1328, laid out as steps by #1424). No React, no
 * requests: where a claim stands, the one next action, what the console may offer, how the wait
 * for the relay is worded, and what a form must contain before it is sent.
 *
 * None of this is enforcement — the server refuses whatever the browser shows — but a button that
 * can only ever fail is a lie, so the two are kept in step here and pinned by tests.
 */

/** The pill beside a domain. Progress means "someone is still working", not "something is wrong". */
export function mailDomainTone(state: MailDomainState): StatusTone {
  switch (state) {
    case "awaitingRecord":
    case "finishing":
      return "progress";
    case "checkUnavailable":
      return "warning";
    case "receiving":
      return "success";
  }
}

/** Asked for, and the relay has simply not accepted both records yet — its own schedule. */
const SENDING_AWAITING: ReadonlySet<MailDomainSendingState> = new Set<MailDomainSendingState>([
  "awaitingRecords",
  "dkimMissing",
  "returnPathMissing",
]);

/** Asked for and not (or no longer) confirmed: the states a sending check can move. */
const SENDING_UNCONFIRMED: ReadonlySet<MailDomainSendingState> = new Set<MailDomainSendingState>([
  ...SENDING_AWAITING,
  "stale",
]);

/** The pill on the sending step. A wait is work in progress; a verdict we could not renew is ours. */
export function mailDomainSendingTone(sending: MailDomainSendingState): StatusTone {
  if (sending === "sending") return "success";
  if (sending === "stale") return "warning";
  return SENDING_AWAITING.has(sending) ? "progress" : "neutral";
}

export type MailDomainStepKey = "receive" | "send" | "done";
export type MailDomainStepStatus = "done" | "current" | "upcoming";

export interface MailDomainStep {
  readonly key: MailDomainStepKey;
  readonly status: MailDomainStepStatus;
}

type StepFacts = Pick<MailDomainView, "state" | "sending">;

/**
 * Which step a claim is on.
 *
 * Receiving comes first because nothing else works without it, and the server refuses to register
 * a sender domain before then. Sending is the second step wherever this installation can send from
 * a business's own domain at all — including while nobody has asked to, because that is where the
 * offer lives. A domain is done once it receives and, where sending exists, replies leave from it.
 */
export function mailDomainCurrentStep(row: StepFacts): MailDomainStepKey {
  if (row.state !== "receiving") return "receive";
  if (row.sending.state === "unavailable" || row.sending.state === "sending") return "done";
  return "send";
}

/**
 * The progress a card shows: three steps, or two where this installation cannot send from a
 * business's own domain — a step nobody can ever complete would be a promise the screen breaks.
 */
export function mailDomainSteps(row: StepFacts): MailDomainStep[] {
  const keys: MailDomainStepKey[] =
    row.sending.state === "unavailable" ? ["receive", "done"] : ["receive", "send", "done"];
  const current = mailDomainCurrentStep(row);
  const at = keys.indexOf(current);
  return keys.map((key, index) => ({
    key,
    status: current === "done" || index < at ? "done" : index === at ? "current" : "upcoming",
  }));
}

export type MailDomainNext =
  | "addRecords"
  | "checkUnavailable"
  | "finishing"
  | "enableSending"
  | "publishSending"
  | "sendingStale";

/**
 * The one sentence at the top of a card: what to do next, or that there is nothing to do.
 *
 * Null once a domain is done, because "next: nothing" is the noise this line exists to cut. Where
 * the next move is ours — a lookup we could not make, a setup we are finishing, a verdict we could
 * not renew — the line says so, so nobody edits a zone that is already right.
 */
export function mailDomainNext(row: StepFacts): MailDomainNext | null {
  switch (row.state) {
    case "awaitingRecord":
      return "addRecords";
    case "checkUnavailable":
      return "checkUnavailable";
    case "finishing":
      return "finishing";
    case "receiving":
      break;
  }
  if (row.sending.state === "notRequested") return "enableSending";
  if (row.sending.state === "stale") return "sendingStale";
  return SENDING_AWAITING.has(row.sending.state) ? "publishSending" : null;
}

/** Which check a card offers. The label differs because the question does. */
export type MailDomainCheckKind = "records" | "sending";

export interface MailDomainActions {
  readonly check: MailDomainCheckKind | null;
  readonly withdraw: boolean;
  /** A receiving domain is taken down by disconnecting it (#1429), with its own confirmation. */
  readonly disconnect: boolean;
}

/**
 * What a business may do with one of its claims.
 *
 * Before a domain receives, a check looks at DNS: on an unproven claim that is the whole point, on
 * a half-finished one it resumes the setup. Once it receives, the proof is no longer looked at
 * (#1424), so the only check left is the relay's verdict — offered while sending is asked for and
 * not confirmed, and nowhere else, because a button that re-reads a settled answer is noise.
 *
 * Withdrawing stops exactly where the server's refusal does — at a domain that is receiving.
 * `finishing` keeps the button on purpose: it is the state a business is left in when the mail
 * server was unreachable mid-setup, and hiding it there would trap them in it with nothing to
 * press. That withdrawal is safe because the route goes through provisioning, which takes the
 * domain off the mail server before the row goes.
 *
 * A receiving domain is disconnected instead (#1429): the same withdrawal on the server, behind a
 * confirmation that names the domain and says what to change in DNS.
 */
export function mailDomainActions(row: StepFacts): MailDomainActions {
  if (row.state !== "receiving") return { check: "records", withdraw: true, disconnect: false };
  return {
    check: SENDING_UNCONFIRMED.has(row.sending.state) ? "sending" : null,
    withdraw: false,
    disconnect: true,
  };
}

/**
 * Whether what somebody typed names this domain (#1429).
 *
 * The server compares again after its own normalisation; this only decides whether the console
 * sends the request or points at the slip. Case, surrounding spaces and one trailing dot are
 * forgiven, because DNS forgives them; anything else is a different name.
 */
export function mailDomainDisconnectConfirmed(typed: string, domain: string): boolean {
  const name = typed.trim().toLowerCase().replace(/[.]$/, "");
  return name.length > 0 && name === domain.toLowerCase();
}

const STEP_RECORD_KINDS: Record<"receive" | "send", readonly MailDomainRecord["kind"][]> = {
  receive: ["mx", "txt"],
  send: ["returnPath", "dkim"],
};

/** The records one step asks for, in the server's order. Names and values pass through untouched. */
export function mailDomainStepRecords(
  records: readonly MailDomainRecord[],
  step: "receive" | "send",
): MailDomainRecord[] {
  return records.filter((record) => STEP_RECORD_KINDS[step].includes(record.kind));
}

export type MailDomainRecordStatus = "confirmed" | "waiting";

/**
 * The relay's verdict on one sending record, or null for a record nobody gives a verdict on.
 *
 * Only the two sending records have one. The relay judges them separately, and naming the one that
 * is still waiting is what stops somebody re-editing the one that is already right.
 */
export function mailDomainRecordStatus(
  record: MailDomainRecord,
  sending: Pick<MailDomainSending, "dkimVerified" | "returnPathVerified">,
): MailDomainRecordStatus | null {
  if (record.kind === "dkim") return sending.dkimVerified ? "confirmed" : "waiting";
  if (record.kind === "returnPath") return sending.returnPathVerified ? "confirmed" : "waiting";
  return null;
}

/** Up to here a wait for the relay is ordinary: it re-checks unconfirmed records every few minutes. */
export const RELAY_WAIT_USUAL_MS = 30 * 60 * 1000;
/** Past this, caching no longer explains it; a record most likely sits under the wrong name. */
export const RELAY_WAIT_LONG_MS = 4 * 60 * 60 * 1000;

export type MailDomainRelayWait = "usual" | "hours" | "long";

/**
 * How long a business has been waiting for the relay, in the three bands the copy distinguishes.
 *
 * The live proof took about five minutes; a resolver that cached "no such record" before the
 * record existed can hold that answer for hours. "Usually within 10 minutes" at hour three is as
 * wrong as "check your records" at minute two, so the sentence follows the clock. A missing or
 * unreadable start reads as the first band — the least alarming thing that is still true.
 */
export function mailDomainRelayWait(requestedAt: string | null, now: number): MailDomainRelayWait {
  const since = requestedAt ? Date.parse(requestedAt) : Number.NaN;
  if (!Number.isFinite(since)) return "usual";
  const elapsed = now - since;
  if (elapsed < RELAY_WAIT_USUAL_MS) return "usual";
  return elapsed <= RELAY_WAIT_LONG_MS ? "hours" : "long";
}

/**
 * The key of the wait sentence on a sending step, or null where there is no wait to explain.
 *
 * The long band names the record that is still missing, because by then waiting is no longer the
 * answer and "check both" would send somebody back to the one that is fine.
 */
export function mailDomainRelayWaitKey(
  sending: MailDomainSendingState,
  wait: MailDomainRelayWait,
): string | null {
  if (!SENDING_AWAITING.has(sending)) return null;
  return wait === "long"
    ? `mailDomain.sending.wait.long.${sending}`
    : `mailDomain.sending.wait.${wait}`;
}

export interface MailDomainElapsed {
  readonly unit: "minute" | "hour" | "day";
  readonly value: number;
}

/**
 * How long ago something happened, in whole units for `Intl.RelativeTimeFormat`, or null for
 * "just now". A time in the future also reads as just now: it is a clock disagreement, and
 * "in 3 minutes" would be a stranger thing to show than a slightly early "just now".
 */
export function mailDomainElapsed(iso: string, now: number): MailDomainElapsed | null {
  const elapsed = now - Date.parse(iso);
  if (!Number.isFinite(elapsed) || elapsed < 60_000) return null;
  const minutes = Math.floor(elapsed / 60_000);
  if (minutes < 60) return { unit: "minute", value: minutes };
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return { unit: "hour", value: hours };
  return { unit: "day", value: Math.floor(hours / 24) };
}

export interface MailDomainCheckToast {
  readonly tone: ToastTone;
  readonly key: string;
  /** Whether the server's own detail belongs under the title. */
  readonly withDetail: boolean;
}

/**
 * What a finished check says, judged against where the claim stood before it.
 *
 * "Now receiving" only on the transition into receiving: repeating it on every sending check of a
 * domain that has received for weeks is how the old screen announced news that was not news. A
 * sending check says whether replies now leave from the domain, and a wait that is still normal
 * reads as information rather than a warning — which is the thing a warning colour gets wrong.
 */
export function mailDomainCheckToast(
  before: Pick<MailDomainView, "state">,
  after: StepFacts,
): MailDomainCheckToast {
  if (before.state === "receiving") {
    if (after.sending.state === "sending") {
      return { tone: "success", key: "mailDomain.toast.sendingConfirmed", withDetail: false };
    }
    if (after.sending.state === "stale") {
      return { tone: "warning", key: "mailDomain.sending.state.stale", withDetail: false };
    }
    return { tone: "info", key: "mailDomain.toast.sendingWaiting", withDetail: false };
  }
  if (after.state === "receiving") {
    return { tone: "success", key: "mailDomain.toast.receiving", withDetail: false };
  }
  return {
    tone: after.state === "checkUnavailable" ? "warning" : "info",
    key: `mailDomain.state.${after.state}`,
    withDetail: true,
  };
}

const RECEIVE_ORDER: Record<Exclude<MailDomainState, "receiving">, number> = {
  checkUnavailable: 0,
  awaitingRecord: 1,
  finishing: 2,
};

function rank(row: StepFacts): number {
  const step = mailDomainCurrentStep(row);
  if (step === "done") return 4;
  if (step === "send" || row.state === "receiving") return 3;
  return RECEIVE_ORDER[row.state];
}

/** What needs attention first, then what is merely unfinished, then what already works. */
export function sortMailDomains(rows: readonly MailDomainView[]): MailDomainView[] {
  return [...rows].sort((a, b) => rank(a) - rank(b) || a.domain.localeCompare(b.domain));
}

/**
 * Only what the browser can know: an empty box has nothing to send. Everything about the shape of
 * a domain — its labels, its length, whether another business already proved it — is the server's
 * verdict, shown as it comes back. The server also trims, lowercases and folds umlauts to their
 * A-label form, so the browser deliberately does none of that: two normalisations that disagree
 * would show a person one name and store another.
 */
export function mailDomainFormErrors(raw: string, localPart: string | null = null): string[] {
  const errors: string[] = [];
  if (!raw.trim()) errors.push("mailDomain.error.domainRequired");
  // Null when a reconnect restores the old address and there is no field to fill (#1446).
  if (localPart !== null && !localPart.trim()) errors.push("mailDomain.error.localPartRequired");
  return errors;
}

/**
 * The name the form prefills (#1446): what a claim without a name gets. The server keeps its own
 * default and decides what an address is called; this only saves typing the usual answer.
 */
export const MAIL_DOMAIN_DEFAULT_LOCAL_PART = "anfragen";

/**
 * The address the form would create, as the person types it (#1446), or null until both halves
 * are there. Display only: the server folds an umlaut domain to its ASCII form and decides whether
 * the name is allowed, and the card shows the address it actually stored.
 */
export function mailDomainAddressPreview(localPart: string, domain: string): string | null {
  const name = localPart.trim().toLowerCase();
  const host = domain.trim().toLowerCase().replace(/[.]$/, "");
  return name && host ? `${name}@${host}` : null;
}

/** The copy for each rule an address name can break (#1446); the server names the rule. */
const LOCAL_PART_REFUSAL_KEYS: Readonly<Record<string, string>> = {
  too_short: "mailDomain.error.localPart.tooShort",
  too_long: "mailDomain.error.localPart.tooLong",
  charset: "mailDomain.error.localPart.charset",
  edge_punctuation: "mailDomain.error.localPart.edgePunctuation",
  adjacent_punctuation: "mailDomain.error.localPart.adjacentPunctuation",
  domain_operation: "mailDomain.error.localPart.domainOperation",
};

export interface MailDomainSendingActions {
  readonly enable: boolean;
  readonly disable: boolean;
}

/**
 * Whether the console may offer to start or stop sending as this domain (#1329).
 *
 * Enabling is offered only once the domain is actually receiving, because the server refuses it
 * before then — a domain that is still waiting for its MX has no mailbox for a reply to leave
 * from, and a button whose only outcome is a refusal is a lie the screen tells.
 *
 * Disabling stays on offer in every requested state, including the one where nothing has been
 * published yet. That state is exactly where somebody changes their mind, and it would be a poor
 * joke to make them publish two records before we let them undo asking for them.
 */
export function mailDomainSendingActions(
  state: MailDomainState,
  sending: MailDomainSendingState,
): MailDomainSendingActions {
  if (sending === "unavailable") return { enable: false, disable: false };
  if (sending === "notRequested") return { enable: state === "receiving", disable: false };
  return { enable: false, disable: true };
}

export interface MailDomainClaimRefusal {
  readonly key: string;
  readonly vars: Record<string, string>;
}

/**
 * The two refusals #1358 added, turned back into German (`decisionSafetyApiErrorCode` pattern).
 *
 * These are the ones a person is most likely to meet and least able to act on from an English
 * sentence: the first tells them their domain already carries somebody's mail and offers the name
 * to use instead, the second tells them nothing was lost. Everything else the server refuses stays
 * as it comes back — a body without one of these codes returns `null` and the caller falls through
 * to `error.message`, so a new server-side refusal shows up rather than disappearing.
 */
export function mailDomainClaimRefusal(error: unknown): MailDomainClaimRefusal | null {
  if (!(error instanceof ApiError) || !error.data || typeof error.data !== "object") return null;
  const data = error.data as Record<string, unknown>;
  const domain = String(data.domain ?? "");
  if (error.status === 409 && data.code === "MAIL_DOMAIN_ALREADY_RECEIVES_MAIL") {
    return {
      key: "mailDomain.error.alreadyReceivesMail",
      vars: {
        domain,
        hosts: Array.isArray(data.hosts) ? data.hosts.join(", ") : "",
        suggestion: String(data.suggestion ?? ""),
        address: String(data.address ?? ""),
      },
    };
  }
  // One mail domain per business (#1430): `domain` is the one already held, to remove first.
  if (error.status === 409 && data.code === "MAIL_DOMAIN_LIMIT_REACHED") {
    return { key: "mailDomain.error.limitReached", vars: { domain } };
  }
  if (error.status === 503 && data.code === "MAIL_DOMAIN_MX_CHECK_UNAVAILABLE") {
    return { key: "mailDomain.error.mxCheckUnavailable", vars: { domain } };
  }
  // #1446: a rule we have words for; an unknown one falls back to the server's own sentence.
  if (error.status === 400 && data.code === "MAIL_DOMAIN_LOCAL_PART_REFUSED") {
    const key = LOCAL_PART_REFUSAL_KEYS[String(data.reason ?? "")];
    return key ? { key, vars: { localPart: String(data.localPart ?? "") } } : null;
  }
  // #1446: the domain was connected before and keeps the address it had.
  if (error.status === 409 && data.code === "MAIL_DOMAIN_ADDRESS_FIXED") {
    return { key: "mailDomain.error.addressFixed", vars: { domain, address: String(data.address ?? "") } };
  }
  return null;
}
